import { useCallback, useMemo } from 'react';
import { BASE_PATH, BASE_PLUS } from '../domain/routes';
import { useQueryParam } from './useQueryParam.hook';

/**
 * Hook that manages URL state parsing.
 *
 * @returns {Object} URL state including pathname, searchParams, and navigation paths.
 *
 * @example
 * const { pathname, isMFE, parentPath, nestedPath } = useUrlState();
 * in isMFE case nestedPath is benefit and nestedModalPath is step
 *  example: /wohnenplus?benefit=nebenkosten-check&step=operational-statement-upload
 *  nestedPath = nebenkosten-check
 *  nestedModalPath = operational-statement-upload
 *
 * in non-MFE case nestedPath is modal path and nestedModalPath is part after that
 *  example: /wohnenplus/nebenkosten-check/operational-statement-upload
 *  nestedPath = nebenkosten-check
 *  nestedModalPath = operational-statement-upload
 */
export function useUrlState() {
  const { pathname, search, origin } = window.location;
  const currentUrl = useMemo(() => `${origin}${pathname}`, [origin, pathname]);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const isMFE = searchParams.has('benefit');
  const benefit = useQueryParam('benefit');
  const step = useQueryParam('step');

  const regex = useMemo(() => new RegExp(`^${BASE_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/?`), []);

  const [parentPath = BASE_PLUS, nestedPath = null, nestedModalPath = null] = useMemo(() => {
    const [parentPath = BASE_PLUS, nestedPath = null, nestedModalPath = null] = pathname
      .replace(regex, '')
      .split('/')
      .filter(Boolean);
    if (isMFE) return [parentPath, benefit, step];
    return [parentPath, nestedPath, nestedModalPath];
  }, [pathname, regex, isMFE, benefit, step]);

  return {
    pathname,
    searchParams,
    isMFE,
    parentPath,
    nestedPath,
    nestedModalPath,
    currentUrl,
  };
}

export const useUrlStateParams = () => {
  const { pathname, search } = window.location;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  // set search params
  const setSearchParams = useCallback(
    (newSearchParams: URLSearchParams) => {
      window.history.pushState(null, '', `${pathname}?${newSearchParams.toString()}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    [pathname],
  );
  return {
    pathname,
    searchParams,
    setSearchParams,
  };
};
